import { AsyncPipe, NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormGroup,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslateModule } from '@ngx-translate/core';
import { IconName } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  Car,
  Dossier,
  Fitter,
  FitterServiceType,
  PartAvailabilityStatus,
  VehicleType,
  VehicleTypeName,
  generateNewCar,
  getCurrentCarDescription,
  getGlassNameKey,
  isSameLicensePlate,
  ErrorMessage,
  HTMLElementEvent,
} from '@sc/data-access-models';
import { StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { FitterRouterService } from '@sc/feature-fitter-path-router-system';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  ButtonComponent,
  CheckboxPlateDirective,
  IconComponent,
  InputTextComponent,
  LicensePlateNumberValidator,
  SelectComponent,
  SpinnerComponent,
} from '@sc/ui-components';
import { dayjs, getTranslatableDuration } from '@sc/utils';
import { BehaviorSubject, Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import {
  PlanningFormSandboxService,
  PlanningSandboxService,
} from '../../../services';

@Component({
  selector: 'sc-planning-form-appointment-intervention',
  styleUrls: ['./planning-form-appointment-intervention.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    InputTextComponent,
    CheckboxPlateDirective,
    NgIf,
    SpinnerComponent,
    MatFormFieldModule,
    IconComponent,
    ButtonComponent,
    NgClass,
    AsyncPipe,
    TranslateModule,
    SelectComponent,
  ],
  template: `
    <ng-container [formGroup]="_form">
      <fieldset>
        <legend>
          {{ 'PlanningFormAppointmentIntervention.title' | translate }}
        </legend>
        <div class="form-row" formGroupName="claim">
          <div class="form-data" formGroupName="car">
            <sc-input-text
              id="licensePlateInput"
              formControlName="licensePlateNumber"
              #licensePlateNumber
              scKeyPress="licensePlate"
              (input)="onValidLicensePlate($any($event).target.value)"
              [label]="
                'PlanningFormAppointmentIntervention.licensePlateLabel'
                  | translate
              "
              [hint]="getLicensePlateTooltipValue()"
              checkboxPlate
              [idCheckbox]="'cb-plate-validation'"
              [classCheckbox]="'mt-m'"
              [currentCar]="
                hasSearchCar || !!_form?.get('id')?.value
                  ? getClaimForm()?.get('car')?.value
                  : null
              "
              [checkboxText]="
                'CustomerDeclaration.LicensePlatePage.onlyConfigPlates'
                  | translate
              "
              (checkboxUpdateEvent)="checkboxPlate($event)"
            ></sc-input-text>
          </div>

          <div class="form-data" *ngIf="!useVehicleTextFieldsWithoutInfoBox()">
            <ng-container
              *ngIf="
                (displayLoading | async) === false &&
                (hasSearchCar ||
                  (currentCar?.model && currentCar?.manufacturer))
              "
            >
              <div
                class="sc-design-text-3 info-box car-name"
                *ngIf="currentCar?.model && currentCar?.manufacturer"
              >
                {{ getCarDisplayInfos() }}
              </div>

              <div
                class="info-box car-name error"
                *ngIf="!currentCar?.model && !currentCar?.manufacturer"
              >
                {{
                  'PlanningFormAppointmentIntervention.vehicleNotFound'
                    | translate
                }}
              </div>
            </ng-container>
            <div class="car-loading" *ngIf="(displayLoading | async) === true">
              <sc-spinner [size]="30"></sc-spinner>
            </div>
          </div>

          <div
            class="form-data"
            formGroupName="car"
            *ngIf="useVehicleTextFieldsWithoutInfoBox()"
          >
            <sc-input-text
              id="manufacturerInput"
              formControlName="manufacturer"
              [label]="
                'PlanningFormAppointmentIntervention.brandLabel' | translate
              "
            ></sc-input-text>
          </div>
        </div>
        <div
          class="form-row"
          formGroupName="claim"
          *ngIf="useVehicleTextFieldsWithoutInfoBox()"
        >
          <div class="form-data" formGroupName="car">
            <sc-input-text
              id="modelInput"
              formControlName="model"
              [label]="
                'PlanningFormAppointmentIntervention.modelLabel' | translate
              "
            ></sc-input-text>
          </div>
          <div class="form-data" formGroupName="car">
            <sc-input-text
              id="vehicleYearInput"
              formControlName="firstRegistration"
              [label]="
                'PlanningFormAppointmentIntervention.vehicleYearLabel'
                  | translate
              "
            ></sc-input-text>
          </div>
        </div>
        <div
          class="form-row"
          formGroupName="claim"
          *ngIf="canDisplayVinNumberInput()"
        >
          <div class="form-data vin-number-container" formGroupName="car">
            <sc-input-text
              id="vinNumberInput"
              formControlName="vinNumber"
              #vinNumber
              scKeyPress="vinNumber"
              [label]="
                'PlanningFormAppointmentIntervention.vinNumberLabel' | translate
              "
            ></sc-input-text>
          </div>
        </div>
        <div class="form-row" formGroupName="claim">
          <div class="form-data" formGroupName="car">
            <mat-label class="title text-grey-dark">{{
              'PlanningFormAppointmentIntervention.vehicleTypeSelection'
                | translate
            }}</mat-label>
            <sc-select
              formControlName="vehicleType"
              class="vehicle-type-select"
              [options]="vehicleTypeList"
              (valueChange)="onVehicleTypeSelect($event)"
            >
              <ng-template #triggerTemplate let-item>
                <sc-icon [name]="getIconFromVehicleType(item)"></sc-icon>
                <span>{{
                  'PlanningFormAppointmentIntervention.vehicleTypeNames.' + item
                    | translate
                }}</span>
              </ng-template>

              <ng-template #optionTemplate let-item>
                <sc-icon [name]="getIconFromVehicleType(item)"></sc-icon>
                <span
                  >{{
                    'PlanningFormAppointmentIntervention.vehicleTypeNames.' +
                      item | translate
                  }}
                </span>
              </ng-template>
            </sc-select>
          </div>
          <div class="form-data sidexa-button-form">
            <span class="space-sidexa">&nbsp;</span>
            <sc-button
              *ngIf="showSidexaBtn()"
              class="sidexa-button"
              (click)="callSidexa()"
              type="button"
              [loading]="displaySidexaLoading | async"
              [disabled]="_form?.disabled || (displaySidexaLoading | async)"
              >{{ 'PlanningFormAppointmentIntervention.sidexaBtn' | translate }}
            </sc-button>
          </div>
        </div>
        <div class="form-row">
          <div class="form-data">
            <mat-label class="title text-grey-dark asterisk">{{
              'PlanningFormAppointmentIntervention.serviceTypeSelection'
                | translate
            }}</mat-label>
            <sc-select
              class="claim-select"
              [value]="getServiceTypeValue()"
              [options]="_fitter?.serviceTypes"
              [disabledOptionFn]="isServiceDisabled"
              [forceDisplayError]="
                _form?.get('appointment').touched &&
                !this._form.get('appointment').get('interventionId')?.value
              "
              [error]="error"
              [disabled]="_form?.disabled"
              panelClass="select-service-type-panel"
              (valueChange)="onServiceTypeSelect($event)"
            >
              <ng-template #triggerTemplate let-item>
                <span
                  class="claim-color"
                  [style.background-color]="item?.color"
                ></span>
                <span>{{ item?.name }} ({{ getServiceDuration(item) }}) </span>
              </ng-template>

              <ng-template #optionTemplate let-item>
                <span
                  class="claim-color"
                  [style.background-color]="item?.color"
                ></span>
                <span>{{ item?.name }} ({{ getServiceDuration(item) }}) </span>
                <span
                  *ngIf="isServiceDisabled(item)"
                  class="disabled-service-type-reason sc-design-text-4"
                  >{{
                    'PlanningFormAppointmentIntervention.serviceTypeDisabledReason'
                      | translate
                  }}</span
                >
              </ng-template>
            </sc-select>
          </div>
          <div class="form-data sinister-details-container">
            <ng-container *ngIf="!!getSinisterDetails()">
              <div class="sc-design-text-3 info-box sinister-details">
                {{ getSinisterDetails() | translate }}
              </div>
            </ng-container>
          </div>
        </div>
        <div class="grey-bg">
          <div
            *ngIf="
              getClaimForm()?.get('eurocode')?.value === null &&
              getClaimForm()?.get('construcorRef')?.value === null &&
              sidexaCalled &&
              sidexaData?.length > 0
            "
            class="form-row"
          >
            <div class="form-data" formGroupName="claim">
              <mat-label
                class="title text-grey-dark"
                *ngIf="sidexaData?.length"
                >{{
                  'PlanningFormAppointmentIntervention.eurocodeSelection'
                    | translate
                }}</mat-label
              >
              <sc-select
                *ngIf="sidexaData?.length"
                class="claim-select"
                [options]="sidexaData"
                (valueChange)="onSidaxaSelect($event)"
              >
                <ng-template #triggerTemplate let-item>
                  <span
                    >{{
                      'PlanningFormAppointmentIntervention.sidexaSelectEurocode'
                        | translate
                          : {
                              hasEuroCode: +!!item?.eurocode,
                              eurocode: item?.eurocode
                            }
                    }}
                  </span>
                </ng-template>

                <ng-template #optionTemplate let-item>
                  <span
                    >{{
                      'PlanningFormAppointmentIntervention.sidexaSelectName'
                        | translate
                          : {
                              name: item?.namePiece,
                              hasName: +!!item?.namePiece
                            }
                    }}
                  </span>
                  <span>{{ '&nbsp;' }}</span>
                  <span
                    >{{
                      'PlanningFormAppointmentIntervention.sidexaSelectRef'
                        | translate
                          : {
                              reference: item?.reference,
                              hasRef: +!!item?.reference
                            }
                    }}
                  </span>
                  <span>{{ '&nbsp;' }}</span>
                  <span
                    >{{
                      'PlanningFormAppointmentIntervention.sidexaSelectEurocode'
                        | translate
                          : {
                              hasEuroCode: +!!item?.eurocode,
                              eurocode: item?.eurocode
                            }
                    }}
                  </span>
                  <span>{{ '&nbsp;' }}</span>
                  <span
                    >{{
                      'PlanningFormAppointmentIntervention.sidexaSelectPrice'
                        | translate
                          : {
                              price: item?.price,
                              hasPrice: +!!item?.price
                            }
                    }}
                  </span>
                </ng-template>
              </sc-select>
            </div>
          </div>
          <div
            class="form-row"
            *ngIf="
              (getClaimForm()?.get('eurocode')?.value !== null ||
                getClaimForm()?.get('constructorRef')?.value !== null) &&
              !sidexaCalled
            "
          >
            <div class="form-data" formGroupName="claim">
              <span
                class="sidexa-data-display"
                *ngIf="getClaimForm()?.get('eurocode')?.value !== null"
                ><mat-label class="title text-grey-dark">{{
                  'PlanningFormAppointmentIntervention.eurocodeDisplay'
                    | translate
                }}</mat-label>

                <strong>
                  {{
                    !!getClaimForm()?.get('eurocode')?.value
                      ? getClaimForm()?.get('eurocode')?.value
                      : ('PlanningFormAppointmentIntervention.uknownCode'
                        | translate)
                  }}
                </strong>
              </span>
            </div>
            <div class="form-data" formGroupName="claim">
              <span
                class="sidexa-data-display"
                *ngIf="getClaimForm()?.get('constructorRef')?.value !== null"
                ><mat-label class="title text-grey-dark">{{
                  'PlanningFormAppointmentIntervention.constructorRefDisplay'
                    | translate
                }}</mat-label>

                <strong>
                  {{
                    !!getClaimForm()?.get('constructorRef')?.value
                      ? getClaimForm()?.get('constructorRef')?.value
                      : ('PlanningFormAppointmentIntervention.uknownCode'
                        | translate)
                  }}
                </strong>
              </span>
            </div>
          </div>
          <div class="form-row" formGroupName="appointment">
            <div class="form-data">
              <mat-label class="title text-grey-dark">{{
                'PlanningFormAppointmentIntervention.partAvailabalitySelection'
                  | translate
              }}</mat-label>
              <sc-select
                class="part-availability-select"
                formControlName="partAvailability"
                [options]="partAvailabilities"
              >
                <ng-template #triggerTemplate let-item>
                  <sc-icon
                    name="operation_vitre"
                    [ngClass]="{
                      unavailable:
                        _form?.get('appointment')?.get('partAvailability')
                          ?.value === 'UNAVAILABLE',
                      ordered:
                        _form?.get('appointment')?.get('partAvailability')
                          ?.value === 'ORDERED',
                      available:
                        _form?.get('appointment')?.get('partAvailability')
                          ?.value === 'AVAILABLE'
                    }"
                  ></sc-icon>
                  {{
                    getPartAvailabilityTranslation(
                      _form?.get('appointment')?.get('partAvailability')?.value
                    )
                  }}
                </ng-template>

                <ng-template #optionTemplate let-item>
                  <sc-icon
                    class="operation-icon"
                    name="operation_vitre"
                    [ngClass]="{
                      unavailable: item === 'UNAVAILABLE',
                      ordered: item === 'ORDERED',
                      available: item === 'AVAILABLE'
                    }"
                  ></sc-icon>
                  {{ getPartAvailabilityTranslation(item) }}
                </ng-template>
              </sc-select>
            </div>
            <div class="form-data">
              <sc-input-text
                id="partProviderInput"
                formControlName="partProvider"
                #partProvider
                [suggestions]="filteredPartProviderList"
                (input)="onPartProviderInputChange($event)"
                (selectSuggestion)="onSelectPartProvider($event)"
                [attr.disabled]="
                  !_form?.get('appointment')?.get('partAvailability')?.value
                "
                [label]="
                  'PlanningFormAppointmentIntervention.partProviderSelection'
                    | translate
                "
              ></sc-input-text>
            </div>
          </div>
        </div>
      </fieldset>
    </ng-container>
  `,
})
export class PlanningFormAppointmentInterventionComponent
  implements OnInit, OnDestroy
{
  @Input() set form(form: FormGroup) {
    this._form = form;
    this.resetLicensePlateNumber(this.currentCar?.vehicleType);
  }
  @Input() dossier: Dossier;
  @Input() set fitters(fitters: Fitter[]) {
    if (fitters?.length) {
      this._fitter = fitters.find(
        (fitter) =>
          fitter?.id === this._form.get('appointment').get('fitterId')?.value
      );
      this._fitters = fitters;
    }
  }
  @Input() set touched(value) {
    if (value) {
      this._form?.get('appointment').markAllAsTouched();
      this._form?.get('claim').markAllAsTouched();
    }
  }

  _form: FormGroup;
  _fitter: Fitter;
  _fitters: Fitter[];

  shouldApplyPlateValidation = true;

  hasSearchCar = false;
  currentCar: Car = null;
  displayLoading = new BehaviorSubject<boolean>(false);

  vehicleTypeListWithIcon: VehicleType[] = [
    { name: 'car', icon: 'car' },
    { name: 'utility', icon: 'utility' },
    { name: 'truck', icon: 'truck' },
    { name: 'tractor', icon: 'tractor' },
    { name: 'bus', icon: 'bus' },
    { name: 'van', icon: 'van' },
    { name: 'publicworksvehicle', icon: 'public_works_vehicle' },
    { name: 'smallcar', icon: 'car' },
    { name: 'other', icon: 'other_vehicle' },
  ];

  vehicleTypeList: string[] = this.vehicleTypeListWithIcon.map(
    (item) => item.name
  );

  partAvailabilities: PartAvailabilityStatus[];
  error: ErrorMessage = {
    title: this.translator.getInstantTranslation(
      'MatErrors.requiredErrorTitle'
    ),
    message: this.translator.getInstantTranslation(
      'MatErrors.requiredErrorMessage',
      {
        fieldName: this.translator.getInstantTranslation(
          'PlanningFormAppointmentIntervention.serviceTypeSelection'
        ),
      }
    ),
  };

  canDoSidexa: boolean;
  sidexaData;
  sidexaCalled = false;
  displaySidexaLoading = new BehaviorSubject<boolean>(false);

  autoCompleteCarSelectionSub: Subscription;
  manualCarInfoSub = new Subscription();

  constructor(
    private readonly planningSandbox: PlanningSandboxService,
    private readonly planningFormSB: PlanningFormSandboxService,
    private readonly translator: TranslatorService,
    private readonly configurationService: ConfigurationService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly routerService: FitterRouterService,
    private readonly storage: StorageService,
    private readonly snackbarService: SnackbarService,
    private readonly featureFlipService: FeatureFlippingManagerService
  ) {}

  partProviderList: string[];
  filteredPartProviderList: string[] = [];

  ngOnInit() {
    this._form
      .get('appointment')
      .get('fitterId')
      .valueChanges?.subscribe((value) => {
        this._fitter = this._fitters.find((fitter) => fitter?.id === value);
        this._form.patchValue({
          appointment: {
            brandId: this._fitter?.brandId,
          },
        });
        this.changeDetector.markForCheck();
      });

    this._form.valueChanges.subscribe(() => this.changeDetector.markForCheck());

    this.initPartAvailabilities();
    this.currentCar = this.getClaimCarForm()?.value;

    this.onValidLicensePlate(this.currentCar?.licensePlateNumber);
    this.partProviderList =
      this.configurationService?.getConfig()?.partProviders;
    this.filteredPartProviderList = this.partProviderList;

    if (this.useVehicleTextFieldsWithoutInfoBox()) {
      for (const field of ['manufacturer', 'model', 'firstRegistration']) {
        const control = this.getClaimCarForm()?.get(field);

        if (control) {
          this.manualCarInfoSub.add(
            control.valueChanges.subscribe(() =>
              this.syncCurrentCarManualInfos()
            )
          );
        }
      }
    }

    this.initShouldApplyPlateValidation();
    this.resetLicensePlateNumber(this.currentCar?.vehicleType);

    this.planningFormSB
      .newServiceTypeSelections()
      .pipe(skip(1))
      .subscribe((newServiceType) => {
        this.onServiceTypeSelect(newServiceType);
      });

    this.autoCompleteCarSelectionSub =
      this.planningSandbox.autoCompleteCarSelection$.subscribe((value) => {
        if (value) {
          this.currentCar = value;
          this.initShouldApplyPlateValidation();
          this.hasSearchCar = this.shouldApplyPlateValidation;
          this.applyLicensePlateValidators(this.currentCar?.vehicleType);

          this.changeDetector.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.planningSandbox.autoCompleteCarSelection$.next(null);
    this.autoCompleteCarSelectionSub?.unsubscribe();
    this.manualCarInfoSub.unsubscribe();
  }

  canDisplayVinNumberInput(): boolean {
    return this.featureFlipService.isFeatureActivated(
      'agenda-can-declare-vin-number'
    );
  }

  initShouldApplyPlateValidation() {
    this.shouldApplyPlateValidation =
      (this.getClaimCarForm()?.get('countryCode')?.value ===
        this.configurationService?.getDefaultLicensePlateCountry() &&
        LicensePlateNumberValidator.pattern(
          this.configurationService.getAllLicensePlateCountries()
        )(this.getClaimCarForm()?.get('licensePlateNumber')) === null) ||
      !this.currentCar?.licensePlateNumber;

    this.changeDetector.markForCheck();
  }

  showSidexaBtn() {
    const dossier: Dossier = this._form?.getRawValue();
    return (
      (dossier?.claim?.glass?.glass === 'windshield' ||
        dossier?.claim?.glass?.glass === 'rearWindow' ||
        dossier?.claim?.glass?.glass === 'sideWindow' ||
        dossier?.claim?.glass?.glass === 'lights' ||
        dossier?.claim?.glass?.glass === 'topWindow') &&
      dossier?.claim?.operationType === 'Replace' &&
      !!this.currentCar?.licensePlateNumber?.length &&
      !!this.currentCar?.model &&
      !!this.currentCar?.manufacturer
    );
  }

  onSidaxaSelect(value) {
    this.getClaimForm()?.get('eurocode')?.setValue(value?.eurocode);
    this.getClaimForm()?.get('constructorRef')?.setValue(value?.reference);
    this.getClaimCarForm()?.updateValueAndValidity();
    this.sidexaData = null;
    this.sidexaCalled = false;
  }

  initPartAvailabilities() {
    this.partAvailabilities = ['AVAILABLE', 'ORDERED', 'UNAVAILABLE'];
  }

  getManualCarInfos(): Partial<Car> {
    const currentCarFormValue = this.getClaimCarForm()?.getRawValue?.();

    return {
      manufacturer: currentCarFormValue?.manufacturer ?? null,
      model: currentCarFormValue?.model ?? null,
      firstRegistration: currentCarFormValue?.firstRegistration ?? null,
    };
  }

  syncCurrentCarManualInfos() {
    this.currentCar = {
      ...(this.currentCar ?? this.getClaimCarForm()?.getRawValue?.() ?? {}),
      ...this.getManualCarInfos(),
    };
    this.changeDetector.markForCheck();
  }

  getLookupCarInfoValue<T>(lookupValue: T, manualValue: T): T {
    if (
      lookupValue === undefined ||
      lookupValue === null ||
      lookupValue === ''
    ) {
      return manualValue ?? null;
    }

    return lookupValue;
  }

  generateCarWithManualInfos(
    car: Partial<Car>,
    preferLookupVehicleInfos = false
  ) {
    const manualCarInfos = this.getManualCarInfos();

    return generateNewCar(
      {
        ...manualCarInfos,
        ...car,
        manufacturer: preferLookupVehicleInfos
          ? this.getLookupCarInfoValue(
              car.manufacturer,
              manualCarInfos.manufacturer
            )
          : car.manufacturer ?? manualCarInfos.manufacturer,
        model: preferLookupVehicleInfos
          ? this.getLookupCarInfoValue(car.model, manualCarInfos.model)
          : car.model ?? manualCarInfos.model,
        firstRegistration:
          car.firstRegistration ?? manualCarInfos.firstRegistration,
      },
      this.configurationService.getAllLicensePlateCountries(),
      this.configurationService.getDefaultLicensePlateCountry()
    );
  }

  generateCarFromFormContext(
    car: Partial<Car>,
    preferLookupVehicleInfos = false
  ) {
    if (this.useVehicleTextFieldsWithoutInfoBox()) {
      return this.generateCarWithManualInfos(car, preferLookupVehicleInfos);
    }

    return generateNewCar(
      car,
      this.configurationService.getAllLicensePlateCountries(),
      this.configurationService.getDefaultLicensePlateCountry()
    );
  }

  onValidLicensePlate(licensePlateNumber: string): void {
    const isSameLicensePlateNumber = isSameLicensePlate(
      this.currentCar?.licensePlateNumber,
      this.getClaimCarForm()?.get('licensePlateNumber')?.value
    );
    if (!isSameLicensePlateNumber) {
      this.getClaimForm()?.get('eurocode')?.setValue(null);
      this.getClaimForm()?.get('constructorRef')?.setValue(null);
      this.sidexaCalled = false;
      this.sidexaData = null;
    }
    this.hasSearchCar = false;

    //Case when licenseplate changes and not valid
    if (
      !isSameLicensePlateNumber &&
      (!this.getClaimCarForm()?.get('licensePlateNumber').valid ||
        this.getClaimCarForm()
          ?.get('licensePlateNumber')
          .hasError('license_plate_error'))
    ) {
      this.currentCar = null;
      const newCar = this.generateCarFromFormContext({
        licensePlateNumber:
          licensePlateNumber ??
          this.getClaimCarForm()?.get('licensePlateNumber')?.value,
        vehicleType: this.getClaimCarForm()?.get('vehicleType')?.value,
        ownerShip: this.getClaimCarForm()?.get('ownerShip')?.value,
      });
      this.getClaimCarForm()?.patchValue(newCar);
      this.getClaimCarForm()?.updateValueAndValidity();
      this.hasSearchCar = false;
    } else {
      //Case when licenseplate changes or no model+manufacturer data and licencseplate is valid
      // Only search for cars that pass the pattern validation even if it is not required for form validity
      const patternResult = LicensePlateNumberValidator.pattern(
        this.configurationService.getAllLicensePlateCountries()
      )(this.getClaimCarForm()?.get('licensePlateNumber'));
      if (
        (!isSameLicensePlateNumber ||
          !(this.currentCar?.manufacturer && this.currentCar?.model)) &&
        !!licensePlateNumber &&
        patternResult === null &&
        this.shouldApplyPlateValidation
      ) {
        this.displayLoading.next(true);
        this.planningSandbox
          .fetchCarData(licensePlateNumber, null)
          .subscribe({
            next: (response) => {
              this.currentCar = this.generateCarFromFormContext(
                {
                  ...response.payload.car,
                  vehicleType:
                    response.payload.car?.vehicleType ??
                    this.getClaimCarForm()?.get('vehicleType')?.value,
                  ownerShip: this.getClaimCarForm()?.get('ownerShip')?.value,
                },
                true
              );
              if (this.currentCar?.vehicleType) {
                this.getClaimCarForm()
                  ?.get('vehicleType')
                  .setValue(this.currentCar.vehicleType);
              }
            },
            error: () => {
              this.currentCar = this.generateCarFromFormContext({
                licensePlateNumber: licensePlateNumber ?? '',
                vehicleType: this.getClaimCarForm()?.get('vehicleType')?.value,
                ownerShip: this.getClaimCarForm()?.get('ownerShip')?.value,
              });
            },
          })
          .add(() => {
            this.displayLoading.next(false);
            this.getClaimCarForm()?.patchValue(this.currentCar);
            this.getClaimCarForm()?.updateValueAndValidity();
            this.hasSearchCar = true;
            this.changeDetector.markForCheck();
          });
      } else {
        //other cases
        this.currentCar =
          isSameLicensePlateNumber && !!this.currentCar?.licensePlateNumber
            ? this.currentCar
            : this.generateCarFromFormContext({
                licensePlateNumber:
                  licensePlateNumber ??
                  this.getClaimCarForm()?.get('licensePlateNumber')?.value ??
                  null,
                vehicleType: this.getClaimCarForm()?.get('vehicleType')?.value,
                ownerShip: this.getClaimCarForm()?.get('ownerShip')?.value,
              });
        this.getClaimCarForm()?.patchValue(this.currentCar);
        this.getClaimCarForm()?.updateValueAndValidity();
      }
    }
    this.changeDetector.markForCheck();
  }

  getCarDisplayInfos() {
    return getCurrentCarDescription(
      this.currentCar,
      this.featureFlipService.isFeatureActivated(
        'display-vehicle-information-year'
      ),
      this.translator.getInstantTranslation(
        'GlobalApplicativeConstants.licensePlateNumberDoorsLabel'
      ),
      dayjs
    );
  }

  useVehicleTextFieldsWithoutInfoBox() {
    return this.featureFlipService.isFeatureActivated(
      'agenda-hide-vehicle-info-box-show-text-fields'
    );
  }

  getSinisterDetails(): string {
    const glass = this.dossier?.claim?.glass;
    if (
      glass?.glass !==
      this._form.get('claim')?.get('glass')?.get('glass')?.value
    )
      return '';

    const hasDetails =
      (glass?.glass === 'sideWindow' && glass?.lateralPrecisions) ||
      (glass?.glass === 'lights' && glass?.light) ||
      (glass?.glass === 'mirror' && glass?.mirror);

    return hasDetails ? getGlassNameKey(glass) : '';
  }

  getLicensePlateTooltipValue() {
    if (!this.isNoValidationVehicleType() && this.shouldApplyPlateValidation) {
      return this.translator.getInstantTranslation(
        'GlobalApplicativeConstants.licensePlateInfoTooltip.' +
          this.configurationService.getDefaultLicensePlateCountry()
      );
    }

    return null;
  }

  checkboxPlate(value) {
    const currentVehicleType =
      this.getClaimCarForm()?.get('vehicleType')?.value || 'car';
    this.applyLicensePlateValidators(currentVehicleType, value);
    if (
      (this.currentCar?.countryCode ===
        this.configurationService?.getDefaultLicensePlateCountry() ||
        !this.currentCar?.countryCode) &&
      (this.hasSearchCar || !!this._form?.get('id')?.value)
    ) {
      this.getClaimCarForm()
        ?.get('countryCode')
        ?.setValue(
          !value
            ? null
            : this.configurationService?.getDefaultLicensePlateCountry()
        );
    }
  }

  onServiceTypeSelect(serviceType: FitterServiceType) {
    const interventionDuration = this.getUsedInterventionDuration(serviceType);

    // Reset eurocode selection if not the same glass or operation selected
    if (
      serviceType?.glassType !==
        this.getClaimForm()?.get('glass')?.get('glass')?.value ||
      (serviceType?.glassType ===
        this.getClaimForm()?.get('glass')?.get('glass')?.value &&
        serviceType?.operationType !==
          this.getClaimForm()?.get('operationType')?.value)
    ) {
      this.getClaimForm()?.get('eurocode')?.setValue(null);
      this.getClaimForm()?.get('constructorRef')?.setValue(null);
      this.getClaimForm().updateValueAndValidity();
      this.sidexaCalled = false;
      this.sidexaData = null;
    }

    // Adapt the end of the appointment depending on the intervention duration
    if (
      this.getAppointmentForm()?.get('time')?.value &&
      !!interventionDuration
    ) {
      const startTime = dayjs.utc(
        this.getAppointmentForm()?.get('time')?.value
      );
      const endTime = startTime?.utc()?.add(interventionDuration, 'minute');

      this.getAppointmentForm()
        ?.get('endTime')
        ?.setValue(endTime?.toISOString(), { emitEvent: false });
    }
    this.getClaimGlassForm().get('glass')?.markAsDirty();
    this.getClaimGlassForm().get('glass').setValue(serviceType?.glassType);
    this.getClaimForm().get('operationType')?.markAsDirty();
    this.getClaimForm()
      .get('operationType')
      .setValue(serviceType?.operationType);

    if (serviceType?.id) {
      this.getAppointmentForm()?.get('interventionId')?.markAsDirty();
      this.getAppointmentForm()?.get('interventionId').setValue(serviceType.id);
    }

    this.getAppointmentForm()
      ?.get('endTime')
      ?.updateValueAndValidity({ onlySelf: false, emitEvent: true });

    this.getClaimForm()?.get('calibration')?.markAsDirty();
    this.getClaimForm()
      ?.get('calibration')
      ?.setValue(serviceType?.calibration ? 'true' : 'false');

    this.changeDetector.markForCheck();
  }

  callSidexa() {
    this.displaySidexaLoading.next(true);
    const fitterId = this._fitter?.id;
    const glassType = this.getClaimGlassForm()?.get('glass')?.value;
    const licensePlateNumber =
      this.getClaimCarForm()?.get('licensePlateNumber')?.value;

    this.planningSandbox
      .getSidexaData(licensePlateNumber, fitterId, glassType)
      ?.subscribe(
        (data) => {
          this.storage.setItem('sidexa-data', data);
          this.sidexaData = this.formatSidexaPiece(data);
          this.routerService.goToSidexaNewTab(licensePlateNumber);
          this.displaySidexaLoading.next(false);
          this.sidexaCalled = true;
          this.changeDetector.markForCheck();
        },
        (error) => {
          this.displaySidexaLoading.next(false);
          this.snackbarService.showError(
            this.translator.getInstantTranslation('Snackbar.errorTitle'),
            this.translator.getInstantTranslation(
              error?.error?.message ||
                'PlanningFormAppointmentIntervention.sidexaDataError'
            )
          );

          console.error(error);
        }
      );
  }

  getPartAvailabilityTranslation(item: PartAvailabilityStatus) {
    return this.translator.getInstantTranslation(
      `GlobalApplicativeConstants.partAvailabilities.${item}`
    );
  }

  getServiceTypeValue() {
    return (this._fitter?.serviceTypes || []).find(
      (serviceType) =>
        serviceType.id ===
        this.getAppointmentForm()?.get('interventionId')?.value
    );
  }

  getUsedInterventionDuration(serviceType: FitterServiceType) {
    return this._form?.get('appointment')?.get('outsideIntervention')?.value
      ? serviceType?.interventionDurationMobility ??
          serviceType?.interventionDuration
      : serviceType?.interventionDuration;
  }

  isServiceDisabled = (serviceType: FitterServiceType) => {
    const isOutside = this.getAppointmentForm()?.get(
      'outsideIntervention'
    )?.value;
    const canDoMobility =
      (isOutside && serviceType.canMakeOutsideIntervention) ||
      (!isOutside && serviceType.canMakeInCenterIntervention) ||
      isOutside === undefined ||
      isOutside === null;

    const hasReplacementVehicle = this.getAppointmentForm()?.get(
      'isReplacementVehicleSelected'
    )?.value;
    const canDoRV =
      !hasReplacementVehicle || serviceType.canShareReplacementVehicle;

    return !canDoMobility || !canDoRV;
  };

  getServiceDuration(item: FitterServiceType) {
    const duration = this.getUsedInterventionDuration(item);
    const durationObj = getTranslatableDuration(duration);
    return this.translator.getInstantTranslation(
      durationObj?.value,
      durationObj.param
    );
  }

  getAppointmentForm(): FormGroup {
    return this._form?.get('appointment') as FormGroup;
  }

  getClaimForm(): FormGroup {
    return this._form?.get('claim') as FormGroup;
  }

  getClaimCarForm(): FormGroup {
    return this.getClaimForm()?.get('car') as FormGroup;
  }

  getClaimGlassForm(): FormGroup {
    return this.getClaimForm()?.get('glass') as FormGroup;
  }

  onVehicleTypeSelect(event) {
    this.currentCar = this.getClaimCarForm()?.value;
    this.resetLicensePlateNumber(event, true);
  }

  isNoValidationVehicleType(vehicleType?: VehicleTypeName) {
    const usedVehicleType: VehicleTypeName =
      vehicleType ??
      this.getClaimCarForm()?.get('vehicleType')?.value ??
      'other';

    return (
      usedVehicleType === 'publicworksvehicle' ||
      usedVehicleType === 'tractor' ||
      usedVehicleType === 'other'
    );
  }

  applyLicensePlateValidators(
    vehicleType: VehicleTypeName,
    newValue?: boolean
  ) {
    this.shouldApplyPlateValidation =
      newValue ?? this.shouldApplyPlateValidation;

    const validators: ValidatorFn[] = [];

    if (!this.isNoValidationVehicleType(vehicleType)) {
      validators.push(Validators.required);
      if (this.shouldApplyPlateValidation) {
        validators.push(
          LicensePlateNumberValidator.pattern(
            this.configurationService.getAllLicensePlateCountries()
          )
        );
      }
    }

    this.getClaimCarForm()
      ?.get('licensePlateNumber')
      ?.setValidators(validators);
    this.getClaimCarForm()?.get('licensePlateNumber')?.updateValueAndValidity();

    if (newValue !== undefined) {
      this.getClaimCarForm()?.get('countryCode')?.markAsDirty();
      const newCountryCode = newValue
        ? this.configurationService?.getDefaultLicensePlateCountry()
        : null;

      // Check if currentCar exists before accessing its properties
      if (this.currentCar) {
        this.currentCar.countryCode = newCountryCode;
      }
      this.getClaimCarForm()?.get('countryCode')?.setValue(newCountryCode);
    }

    if (
      this.shouldApplyPlateValidation &&
      !this.hasSearchCar &&
      this.currentCar &&
      !!this.currentCar.licensePlateNumber
    ) {
      this.onValidLicensePlate(this.currentCar.licensePlateNumber);
    }
  }

  resetLicensePlateNumber(
    vehicleType: VehicleTypeName,
    removeIfChange = false
  ) {
    if (vehicleType) {
      if (
        this.isNoValidationVehicleType(vehicleType) &&
        this.shouldApplyPlateValidation &&
        removeIfChange
      ) {
        this.getClaimCarForm()?.get('licensePlateNumber')?.setValue('');
        if (!this.useVehicleTextFieldsWithoutInfoBox()) {
          this.getClaimCarForm()?.get('model')?.setValue('');
          this.getClaimCarForm()?.get('manufacturer')?.setValue('');
        }
        if (this.currentCar) {
          this.currentCar.licensePlateNumber = '';
          if (!this.useVehicleTextFieldsWithoutInfoBox()) {
            this.currentCar.model = '';
            this.currentCar.manufacturer = '';
          }
        }
        this.getClaimCarForm().updateValueAndValidity();
      }

      this.applyLicensePlateValidators(vehicleType);
    }
  }

  getIconFromVehicleType(item: string): IconName {
    return this.vehicleTypeListWithIcon.find(
      (vehicleType) => vehicleType.name === item
    )?.icon as IconName;
  }

  onSelectPartProvider($event) {
    this.getAppointmentForm().get('partProvider')?.setValue($event);
  }

  onPartProviderInputChange(event: Event) {
    const value = (event as HTMLElementEvent<HTMLInputElement>)?.target?.value;

    this.filteredPartProviderList = this.configurationService.filterSuggestions(
      this.partProviderList,
      value
    );
  }

  formatSidexaPiece(data: any) {
    let listPiece = data?.dossierua?.services?.svccp?.listcppiece?.cppiece;
    if (!listPiece) {
      listPiece =
        data?.dossierua?.services?.svccp?.listcpmodele?.cpmodele?.listcppiece
          ?.cppiece;
    }
    if (Array.isArray(listPiece)) {
      return listPiece
        ?.filter(
          (piece) => piece?.caractec?.listctecnc?.ctecnc?.ctecncval?.length
        )
        ?.map((piece) => {
          return {
            codePiece: piece?.codpiece,
            namePiece: piece?.libpiece,
            reference: piece?.ref,
            price: piece?.prix?.devise?.valeurs?.ht,
            eurocode: piece?.caractec?.listctecnc?.ctecnc?.ctecncval,
            typeCode: piece?.sgn,
          };
        });
    } else {
      const piece = listPiece;
      return [
        {
          codePiece: piece?.codpiece,
          namePiece: piece?.libpiece,
          reference: piece?.ref,
          price: piece?.prix?.devise?.valeurs?.ht,
          eurocode: piece?.caractec?.listctecnc?.ctecnc?.ctecncval,
          typeCode: piece?.sgn,
        },
      ];
    }
  }
}
